import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import settingService from '../../services/settings';
import informationService from '../../services/rest/information';

const initialState = {
  loading: false,
  settings: {},
  error: '',
};

export const fetchSettings = createAsyncThunk(
  'settings/fetchSettings',
  async (params = {}) => {
    const generalRes = await settingService.getGeneral({ ...params });
    let storeRes = { data: {} };
    try {
      storeRes = await settingService.getStore({ ...params });
    } catch (_) {
      storeRes = { data: {} };
    }

    return {
      data: {
        general: generalRes?.data ?? {},
        store: storeRes?.data ?? {},
      },
    };
  }
);
export const fetchRestSettings = createAsyncThunk(
  'settings/fetchRestSettings',
  async (params = {}) => {
    const token =
      typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) {
      // Login screen / public context: avoid protected settings endpoint.
      return informationService.settingsInfo({ ...params }).then((res) => res);
    }
    try {
      const generalRes = await settingService.getGeneral({ ...params });
      let storeRes = { data: {} };
      try {
        storeRes = await settingService.getStore({ ...params });
      } catch (_) {
        storeRes = { data: {} };
      }
      return {
        data: {
          general: generalRes?.data ?? {},
          store: storeRes?.data ?? {},
        },
      };
    } catch (e) {
      return informationService.settingsInfo({ ...params }).then((res) => res);
    }
  }
);

const settingSlice = createSlice({
  name: 'settings',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchSettings.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchSettings.fulfilled, (state, action) => {
      const { payload } = action;
      state.loading = false;
      state.settings = createSettings(payload?.data ?? payload ?? {});
      state.error = '';
    });
    builder.addCase(fetchSettings.rejected, (state, action) => {
      state.loading = false;
      state.settings = {};
      state.error = action.error.message;
    });

    builder.addCase(fetchRestSettings.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchRestSettings.fulfilled, (state, action) => {
      const { payload } = action;
      state.loading = false;
      state.settings = createSettings(payload?.data ?? payload ?? {});
      state.error = '';
    });
    builder.addCase(fetchRestSettings.rejected, (state, action) => {
      state.loading = false;
      state.settings = {};
      state.error = action.error.message;
    });
  },
});

function createSettings(list) {
  if (
    list &&
    typeof list === 'object' &&
    !Array.isArray(list) &&
    (list.general || list.store)
  ) {
    const general = list.general || {};
    const store = list.store || {};
    return {
      ...general,
      ...store,
      title: general.siteTitle ?? '',
      siteTitle: general.siteTitle ?? '',
      logo: store.logo ?? '',
      favicon: store.favicon ?? '',
      currency_symbol: store.currencySymbol ?? '',
      currencySymbol: store.currencySymbol ?? '',
      by_subscription: Boolean(general.bySubscription),
      bySubscription: Boolean(general.bySubscription),
      service_fee: general.serviceFee ?? 0,
      serviceFee: general.serviceFee ?? 0,
      referral_active: Boolean(general.referralActive),
      referralActive: Boolean(general.referralActive),
      maintenance_mode: Boolean(general.maintenanceMode),
      maintenanceMode: Boolean(general.maintenanceMode),
      timezone: general.timezone ?? '',
      tax_rate: store.taxRate ?? 0,
      taxRate: store.taxRate ?? 0,
      shipping_fee: store.shippingFee ?? 0,
      shippingFee: store.shippingFee ?? 0,
      free_shipping_threshold: store.freeShippingThreshold ?? 0,
      freeShippingThreshold: store.freeShippingThreshold ?? 0,
      standard_shipping_fee: store.standardShippingFee ?? 0,
      standardShippingFee: store.standardShippingFee ?? 0,
      store_name: store.storeName ?? '',
      storeName: store.storeName ?? '',
      contact_email: store.contactEmail ?? '',
      contactEmail: store.contactEmail ?? '',
      phone_number: store.phoneNumber ?? '',
      phoneNumber: store.phoneNumber ?? '',
      address: store.address ?? '',
    };
  }
  if (list == null) return {};
  if (Array.isArray(list)) {
    const result = list.map((item) => ({
      [item.key]: item.value,
    }));
    return Object.assign({}, ...result);
  }
  if (typeof list === 'object' && !Array.isArray(list)) {
    return { ...list };
  }
  return {};
}

export default settingSlice.reducer;
